/* eslint-disable react/no-array-index-key */
import { Inline } from '@bedrock-layout/primitives'
import {
  FilterReactAria,
  FullWidthTextSection,
  Heading,
  PageLinkCard,
  Paragraph,
  TextColumns,
  WysiwygBlock,
} from '@okam/brand-ui'
import type { TPatternVariants } from '@okam/brand-ui/index'
import { extractRelativePath } from '@okam/html2react/index'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import type { Key } from 'react'
import { useEffect, useState } from 'react'
import { Item, Section } from 'react-stately'
import { theme } from 'twin.macro'
import type { TFlexibleContent } from '@/components/flexible-content/types'
import { MainLayout } from '@/components/layout'
import { useFragment } from 'gql'
import type {
  ExpertisesQuery,
  AllExpertisesQuery,
  SiteOptionsQuery,
  MainMenuFragment,
  ExpertiseTypeListingQuery,
} from 'gql/graphql'
import { allMembersFragment } from 'query/fragments'
import FlexibleContentDispatcher from '../../../components/flexible-content/FlexibleContentDispatcher/FlexibleContentDispatcher'
import { Html2React } from '../../post/pages/PostPage'

export interface TExpertiseArchivePage {
  pageProps: ExpertisesQuery & AllExpertisesQuery & SiteOptionsQuery & ExpertiseTypeListingQuery
}

const ExpertiseArchivePage = ({ pageProps }: TExpertiseArchivePage) => {
  const { t } = useTranslation('common')
  const pattern = pageProps?.page?.pageExpertiseFields?.pageexpertisePattern as TPatternVariants
  const description = pageProps?.page?.pageExpertiseFields?.pageexpertiseDescription
  const okamContent = pageProps.page?.flexibleContent?.okamContent as Array<TFlexibleContent>
  const expertise = pageProps.expertiseTypes?.nodes ?? []
  const allMembers = useFragment(allMembersFragment, pageProps.members)
  const expertiseData = pageProps.expertises?.nodes ?? []
  const router = useRouter()
  const { query } = router
  const initialSelectedValue = query?.expertisetype as string
  const [selectedValue, setSelectedValue] = useState<string | number>(initialSelectedValue)

  const filterAriaLabel = t('ARIA.FILTER')
  const filterPlaceholder = t('FILTER.BY')
  const expertiseTranslation = t('EXPERTISE.TITLE')

  const filteredExpertises = expertiseData.filter((exp) => {
    const hasSelectedValue = exp?.expertiseTypes?.nodes?.some((type) => type?.slug === selectedValue)
    return !selectedValue || hasSelectedValue
  })

  useEffect(() => {
    setSelectedValue(initialSelectedValue)
  }, [initialSelectedValue])

  return (
    <MainLayout
      seo={pageProps.page?.seo}
      siteOptionsProps={pageProps.siteOptions}
      menuItems={pageProps.mainMenu as MainMenuFragment}
      allMembers={allMembers}
      variant="white"
      translatedUri={pageProps?.page?.translatedUri}
    >
      <header>
        <FullWidthTextSection
          tw="p-14 w-1/3"
          colorTheme={theme('colors.tertiary[6]')}
          minHeight="400px"
          xAlign="left"
          yAlign="bottom"
          patternPosition="right"
          bgPattern={pattern ?? 'angles'}
        >
          <Heading as="h1" variant="h1" tw="uppercase font-semibold text-white w-full">
            {pageProps?.page?.title ?? ''}
          </Heading>
          {pageProps?.page?.content && (
            <Paragraph as="h2" variant="p" tw="text-white">
              <WysiwygBlock content={pageProps?.page?.content} />
            </Paragraph>
          )}
        </FullWidthTextSection>
      </header>
      <Inline
        justify="center"
        tw="flex flex-col h-full py-10 lg:py-16 px-6 mx-auto md:px-12 lg:px-14 w-full max-w-[1920px]"
      >
        {description && (
          <div tw="pb-10 lg:pb-16 prose prose-woods">
            <TextColumns nbCols={1} title="">
              <Html2React content={description} />
            </TextColumns>
          </div>
        )}
        <Heading variant="h2" as="h2" tw="font-semibold text-tertiary-5 w-full mb-6 lg:mb-10">
          {t('EXPERTISE.TITLE')}
        </Heading>
        <div tw="mb-10">
          <FilterReactAria
            id="expertise-filter"
            label={expertiseTranslation}
            name="expertise-filter"
            placeholderLabel={filterPlaceholder}
            ariaLabel={filterAriaLabel}
            onSelectionChange={(value: Key) => setSelectedValue(value)}
            selectedKey={selectedValue}
          >
            <Section title={expertiseTranslation}>
              {expertise &&
                expertise.map((exp) => (
                  <Item textValue={exp.name ?? ''} key={exp.slug}>
                    <Paragraph variant="p" as="p">
                      {exp.name}
                    </Paragraph>
                  </Item>
                ))}
            </Section>
          </FilterReactAria>
        </div>
        <div tw="w-full items-start grid grid-cols-1 md:grid-cols-2 gap-6 lg:grid-cols-4 pb-12 xl:pb-32">
          {filteredExpertises?.map((exp) => (
            <PageLinkCard
              key={exp.id}
              title={exp?.title ?? ''}
              text={exp?.content ?? ''}
              permalink={exp?.link ? extractRelativePath(exp.link) : ''}
              ctaText={t('EXPERTISE.CARD_CTA')}
              ariaLabel={t('ARIA.GO_TO', { page: exp?.title ?? '' })}
              tw="!self-end"
            />
          ))}
        </div>
      </Inline>
      {okamContent.map((content: TFlexibleContent, index: number) => (
        <FlexibleContentDispatcher
          allMembers={allMembers}
          content={content}
          key={`expertise-flexibleContent-${content?.fieldGroupName}-${index}`}
        />
      ))}
    </MainLayout>
  )
}

export default ExpertiseArchivePage
